import React, { useCallback } from "react";
import { Box, LinearProgress, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { HighlightOff, FileOpen } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";

import { useAppDispatch, useAppSelector } from "../../index";
import { clearError, dropFile } from "@store/fileParsing/actions";
import { ApplicationState } from "store/index";
import useLanguageHook from "containers/hooks/useLanguageHook";

function MyDropzone() {
  const dispatch = useAppDispatch();
  const { t } = useLanguageHook();
  const theme = useTheme();

  const files = useAppSelector((state: ApplicationState) => state.fileParsing.file);
  const error = files.filter(({ error }) => error !== null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      // Do something with the files
      dispatch(dropFile(acceptedFiles));
    },
    [dispatch],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/zip": [".zip"],
      "application/octet-stream": [".zip"],
      "application/x-zip-compressed": [".zip"],
    },
  });

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: theme.spacing(2),
          borderWidth: 3,
          borderRadius: 2,
          borderColor: error.length !== 0 ? theme.palette.error.main : theme.palette.primary.main,
          borderStyle: "dashed",
          position: "relative",
          height: "100%",
          //color: theme.palette.primary.main,
          outline: "none",
          cursor: "pointer",
          transition: "all .2s ease-in-out",
          //minWidth: '200px',
          "&:hover": {
            //background: theme.palette.primary.dark,
            borderColor: theme.palette.primary.contrastText,
          },
          fontSize: "1.1em",
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        {renderText()}
      </Box>
    </Box>
  );

  function renderText(): JSX.Element {
    if (isDragActive)
      return (
        <>
          <Typography>{t.Dropzone.Drop}.</Typography>
        </>
      );
    if (files.length > 0) {
      return (
        <>
          <Typography>{t.Dropzone.Loading}</Typography>
          {files.map((d) => (
            <React.Fragment key={d.id}>
              <Typography>
                {d.file.name} {t.Dropzone.Processed}.
              </Typography>
              {d.loading && (
                <LinearProgress
                  sx={{ width: "100%", height: 18, borderRadius: 2 }}
                  variant="determinate"
                  value={d.progress}
                />
              )}
              {d.error && (
                <Typography color="warning">
                  {String(error)} {renderErrorClear()}
                </Typography>
              )}
              {d.progress === 100 && (
                <>
                  <Typography>{t.Dropzone.Add}.</Typography>
                </>
              )}
            </React.Fragment>
          ))}
        </>
      );
    }
    return (
      <>
        <Typography
          color="primary"
          variant="h4"
          align="center"
          sx={{
            textWrap: "balance",
          }}
        >
          <FileOpen sx={{ mr: 1 }} />
          {t.Dropzone.Enter1}{" "}
          <Box
            sx={{
              background: theme.palette.background.default,
              paddingLeft: theme.spacing(1),
              paddingRight: theme.spacing(1),
              borderRadius: "10%",
              display: "initial",
            }}
          >
            .zip
          </Box>
          {t.Dropzone.Enter2}.
        </Typography>
        <Typography color="warning">{t.Dropzone.NoUpload}</Typography>
      </>
    );
  }
  function renderErrorClear() {
    return (
      <Button
        color="primary"
        onClick={(e) => {
          e.stopPropagation();
          dispatch(clearError());
        }}
      >
        <HighlightOff />
      </Button>
    );
  }
}

export default MyDropzone;
